import { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { TagIcon } from './TagIcon';
import {
  PHOSPHOR_ICON_CATEGORIES,
  ALL_PHOSPHOR_ICONS,
  parseTagIcon,
  toPhosphorTagIcon,
  toCustomTagIcon,
} from '../utils/tagIcons';

interface IconPickerProps {
  value: string;
  onChange: (icon: string) => void;
  color?: string;
}

const MAX_UPLOAD_BYTES = 1024 * 1024; // 1MB original file limit
const TARGET_ICON_SIZE = 64;

export function IconPicker({ value, onChange, color }: IconPickerProps) {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parsedValue = parseTagIcon(value);
  const selectedPhosphor = parsedValue.kind === 'phosphor' ? parsedValue.name : null;

  const filteredIcons = useMemo(() => {
    const query = search.toLowerCase().trim();
    if (!query && !activeCategory) return ALL_PHOSPHOR_ICONS;
    if (activeCategory && !query) {
      return PHOSPHOR_ICON_CATEGORIES[activeCategory as keyof typeof PHOSPHOR_ICON_CATEGORIES] || [];
    }
    return ALL_PHOSPHOR_ICONS.filter((icon) => icon.includes(query));
  }, [search, activeCategory]);

  const categories = Object.keys(PHOSPHOR_ICON_CATEGORIES);

  const handleUploadPng = async (file: File) => {
    setUploadError(null);

    if (file.type !== 'image/png') {
      setUploadError('Only PNG files are supported.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError('PNG is too large. Please use a file under 1MB.');
      return;
    }

    try {
      const optimized = await optimizePngToDataUrl(file, TARGET_ICON_SIZE);
      onChange(toCustomTagIcon(optimized));
    } catch {
      setUploadError('Failed to process PNG. Try another file.');
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-lg text-text-muted">
            search
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (e.target.value) setActiveCategory(null);
            }}
            placeholder="Search Phosphor icons..."
            className="input-base pl-10"
          />
        </div>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="btn-secondary px-3 py-2 text-xs whitespace-nowrap"
          title="Upload custom PNG icon"
        >
          Upload PNG
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            void handleUploadPng(file);
            e.currentTarget.value = '';
          }}
        />
      </div>

      {parsedValue.kind === 'custom' && (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-input-bg px-3 py-2">
          <div
            className="flex size-9 items-center justify-center rounded-md"
            style={{ backgroundColor: color || '#111418' }}
          >
            <TagIcon icon={value} size={20} />
          </div>
          <div className="flex-1 text-xs text-text-secondary">
            Custom PNG selected
          </div>
          <button
            type="button"
            onClick={() => onChange(toPhosphorTagIcon('map-pin'))}
            className="text-xs font-semibold text-text-secondary hover:text-text-primary"
          >
            Use Phosphor
          </button>
        </div>
      )}

      {uploadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {uploadError}
        </div>
      )}

      <div className="flex gap-1 flex-wrap">
        <button
          type="button"
          onClick={() => setActiveCategory(null)}
          className={clsx(
            'px-2.5 py-1 text-[11px] font-semibold rounded-md cursor-pointer transition-all border-none',
            !activeCategory
              ? 'bg-text-primary text-white'
              : 'bg-input-bg text-text-secondary hover:text-text-primary'
          )}
        >
          All
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => {
              setActiveCategory(cat);
              setSearch('');
            }}
            className={clsx(
              'px-2.5 py-1 text-[11px] font-semibold rounded-md cursor-pointer transition-all border-none',
              activeCategory === cat
                ? 'bg-text-primary text-white'
                : 'bg-input-bg text-text-secondary hover:text-text-primary'
            )}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-4 gap-1.5 max-h-[260px] overflow-y-auto custom-scrollbar p-1 -m-1">
        {filteredIcons.map((iconName) => {
          const isSelected = selectedPhosphor === iconName;
          const iconValue = toPhosphorTagIcon(iconName);

          return (
            <button
              key={iconName}
              type="button"
              onClick={() => onChange(iconValue)}
              title={iconName}
              className={clsx(
                'group relative flex flex-col items-center justify-center gap-1 rounded-lg cursor-pointer transition-all border-2 px-1 py-2 min-h-[58px]',
                isSelected
                  ? 'border-text-primary bg-text-primary text-white shadow-md scale-[1.02]'
                  : 'border-transparent bg-input-bg text-text-secondary hover:bg-slate-200 hover:text-text-primary'
              )}
              style={isSelected && color ? { backgroundColor: color, borderColor: color } : undefined}
            >
              <TagIcon icon={iconValue} size={16} whiteTint={isSelected} />
              <span className="text-[10px] font-semibold leading-none truncate max-w-full">
                {iconName}
              </span>
            </button>
          );
        })}
      </div>

      {filteredIcons.length === 0 && (
        <div className="flex items-center justify-center py-6 text-sm text-text-muted">
          No Phosphor icons found
        </div>
      )}

      <p className="text-[11px] text-text-muted">
        Uses Phosphor Duotone icons for map rendering. Upload a PNG if you need a custom icon.
      </p>
    </div>
  );
}

async function optimizePngToDataUrl(file: File, targetSize: number): Promise<string> {
  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImage(dataUrl);

  const canvas = document.createElement('canvas');
  canvas.width = targetSize;
  canvas.height = targetSize;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2D context');
  ctx.clearRect(0, 0, targetSize, targetSize);

  const scale = Math.min(targetSize / img.width, targetSize / img.height);
  const drawW = img.width * scale;
  const drawH = img.height * scale;
  const x = (targetSize - drawW) / 2;
  const y = (targetSize - drawH) / 2;

  ctx.drawImage(img, x, y, drawW, drawH);
  return canvas.toDataURL('image/png');
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') resolve(result);
      else reject(new Error('Invalid file result'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

export { ALL_PHOSPHOR_ICONS, PHOSPHOR_ICON_CATEGORIES };
